import { useState } from "react";
import { TrashIcon, Edit3Icon, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { delay, ToastMessage, useDebounce } from "../../../Components/Utils";
import { Button } from "../../../Components/Buttons";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";
import {
  useFetchUnitsQuery,
  useFetchTrashedUnitsQuery,
  useDeleteUnitMutation,
  useDestroyUnitMutation,
  useRestoreUnitMutation,
  useBulkDeleteUnitsMutation,
  useBulkDestroyUnitsMutation,
  useBulkRestoreUnitsMutation,
} from "../../../stores/api/units.api";
import payloadHelper from "../../../stores/api/helpers/payload.helper";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const UnitList = () => {
  const navigate = useNavigate();
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const debouncedSearch  = useDebounce(search,500)
  const queryParams = payloadHelper.forList({
    page,
    limit,
    search: debouncedSearch,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchUnitsQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedUnitsQuery(queryParams, { skip: !trashed });
  const { data: units = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [deleteUnit] = useDeleteUnitMutation();
  const [destroyUnit] = useDestroyUnitMutation();
  const [restoreUnit] = useRestoreUnitMutation();
  const [bulkDeleteUnits] = useBulkDeleteUnitsMutation();
  const [bulkDestroyUnits] = useBulkDestroyUnitsMutation();
  const [bulkRestoreUnits] = useBulkRestoreUnitsMutation();

  const resetPage = () => setPage(1);

  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "restore" : "delete";
    ToastMessage({
      type: "warning",
      message: `You sure you wanna ${verb} this Unit?`,
      title: `Unit ${verb === "delete" ? "Delete" : "Restore"}..!`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") {
            await restoreUnit(row.id).unwrap();
          } else if (type === "destroy") {
            await destroyUnit(row.id).unwrap();
          } else {
            await deleteUnit(row.id).unwrap();
          }
          ToastMessage({
            type: "success",
            title: `Unit ${type === "restore" ? "Restored" : "Deleted"}!`,
            message: `${type === "restore" ? "Restored" : "Deleted"} Unit: ${row.name}`,
          });
        } catch (err) {
          handleError(err, "Failed to update user.");
        }
      },
    });
  };

  const handleMultiple = (selectedItems, type = "delete") => {
    const verb = type === "restore" ? "restore" : "delete";
    ToastMessage({
      type: "warning",
      message: `You sure you wanna ${verb} these Units?`,
      title: `Units ${verb === "delete" ? "Delete" : "Restore"}..!`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(selectedItems);
          if (type === "restore") {
            await bulkRestoreUnits(body).unwrap();
          } else if (type === "destroy") {
            await bulkDestroyUnits(body).unwrap();
          } else {
            await bulkDeleteUnits(body).unwrap();
          }
          ToastMessage({
            type: "success",
            title: `${type === "restore" ? "Restored" : "Deleted"} selected units`,
            message: `${selectedItems.length} user(s) ${type === "restore" ? "restored" : "deleted"} successfully.`,
          });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  const headers = [
    { key: "name", label: "Name", sortable: true },
    { key: "district.name", label: "District", sortable: true },
    { key: "no_of_staff", label: "Total Staff", sortable: true },
    { key: "monitoring_officer.name", label: "Monitoring Officer", sortable: true },
    {
      key: "status",
      label: "Status",
      type: "status",
    },
  ];
  
  const renderActions = (row) => (
    <div className="flex gap-2">
      {!trashed ? (
        <Button
          onClick={() => navigate(ROUTE_PATHS.UNIT_CREATE, { state: { unit_details: row } })}
          title="Edit"
          icon={Edit3Icon}
          className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
        />
      ) : (
        <Button
          onClick={() => handleSingle(row, "restore")}
          title="Restore"
          icon={RotateCcw}
          className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
        />
      )}
      <Button
        onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
        title="Delete"
        icon={TrashIcon}
        className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
      />
    </div>
  );

  return (
    <>
      <BreadCrumb title="Units" />
      <TableListing
        headers={headers}
        paginationData={units}
        loading={isFetching}
        createButton
        createLink={ROUTE_PATHS.UNIT_CREATE}
        actions={renderActions}
        handleBulk={handleMultiple}
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        statusModule={"unit"}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
      />
    </>
  );
};

export default UnitList;
